/**
 * Synchronous React Query cache patches for transfer pre-pack mutations.
 * Avoids a full workspace refetch after every carton line change.
 */

import type { TransferPackAllocation, TransferPackBox, TransferPackWorkspace } from "./prepackApi";

function findPrimaryOpenTransferBox(boxes: readonly TransferPackBox[]): TransferPackBox | null {
  let primary: TransferPackBox | null = null;
  for (const box of boxes) {
    if (box.sealed) continue;
    if (primary == null || box.box_number < primary.box_number) {
      primary = box;
    }
  }
  return primary;
}

function findUnsealedAllocationBox(
  workspace: TransferPackWorkspace,
  allocationId: string,
): { box: TransferPackBox; allocation: TransferPackAllocation } | null {
  for (const box of workspace.boxes) {
    if (box.sealed) continue;
    const allocation = box.allocations.find((row) => String(row.id) === String(allocationId));
    if (allocation != null) {
      return { box, allocation };
    }
  }
  return null;
}

function recomputePackFlags(
  workspace: TransferPackWorkspace,
): Pick<TransferPackWorkspace, "is_fully_packed" | "all_boxes_sealed"> {
  const allBoxesSealed = workspace.boxes.length > 0 && workspace.boxes.every((box) => box.sealed);
  const fullyAllocated = workspace.lines.length > 0
    && workspace.lines.every((line) => line.unallocated_quantity <= 0);
  return {
    all_boxes_sealed: allBoxesSealed,
    is_fully_packed: workspace.pending_pack_qty_request_count === 0
      && fullyAllocated
      && allBoxesSealed,
  };
}

function patchLineQuantities(
  lines: TransferPackWorkspace["lines"],
  transferOrderItemId: number,
  quantityDelta: number,
): TransferPackWorkspace["lines"] {
  return lines.map((line) => {
    if (line.id !== transferOrderItemId) return line;
    const allocated = line.allocated_quantity + quantityDelta;
    return {
      ...line,
      allocated_quantity: allocated,
      unallocated_quantity: Math.max(0, line.pack_target - allocated),
    };
  });
}

function upsertOpenBox(
  workspace: TransferPackWorkspace,
  box: { id: number; box_number: number },
  allocation: TransferPackAllocation,
): { boxes: TransferPackBox[]; open_box: TransferPackBox } {
  const existing = workspace.boxes.find((row) => row.id === box.id);
  if (existing != null) {
    const prior = existing.allocations.find(
      (row) => row.transfer_order_item_id === allocation.transfer_order_item_id,
    );
    const allocations = prior != null
      ? existing.allocations.map((row) => (
          row.transfer_order_item_id === allocation.transfer_order_item_id ? allocation : row
        ))
      : [...existing.allocations, allocation];
    const openBox: TransferPackBox = {
      ...existing,
      allocations,
    };
    return {
      boxes: workspace.boxes.map((row) => (row.id === box.id ? openBox : row)),
      open_box: openBox,
    };
  }
  const openBox: TransferPackBox = {
    id: box.id,
    box_number: box.box_number,
    sealed: false,
    sealed_at: null,
    label_printed_at: null,
    allocations: [allocation],
  };
  return {
    boxes: [...workspace.boxes, openBox],
    open_box: openBox,
  };
}

export function applyTransferPackAllocationAdded(
  workspace: TransferPackWorkspace,
  allocation: TransferPackAllocation,
  box: { id: number; box_number: number },
  addedQuantity: number,
): TransferPackWorkspace {
  const lines = patchLineQuantities(workspace.lines, allocation.transfer_order_item_id, addedQuantity);
  const { boxes } = upsertOpenBox(workspace, box, allocation);
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    lines,
    boxes,
    open_box,
    ...recomputePackFlags({ ...workspace, lines, boxes }),
  };
}

export function applyTransferPackAllocationUpdated(
  workspace: TransferPackWorkspace,
  allocationId: string,
  quantity: number,
): TransferPackWorkspace | null {
  const located = findUnsealedAllocationBox(workspace, allocationId);
  if (located == null) return null;
  const { box: targetBox, allocation: existing } = located;
  const quantityDelta = quantity - existing.quantity;
  if (quantityDelta === 0) return workspace;

  const updatedAllocation: TransferPackAllocation = { ...existing, quantity };
  const boxNext: TransferPackBox = {
    ...targetBox,
    allocations: targetBox.allocations.map((row) => (
      String(row.id) === String(allocationId) ? updatedAllocation : row
    )),
  };
  const boxes = workspace.boxes.map((row) => (row.id === targetBox.id ? boxNext : row));
  const lines = patchLineQuantities(workspace.lines, existing.transfer_order_item_id, quantityDelta);
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    lines,
    boxes,
    open_box,
    ...recomputePackFlags({ ...workspace, lines, boxes }),
  };
}

export function applyTransferPackAllocationRemoved(
  workspace: TransferPackWorkspace,
  allocationId: string,
): TransferPackWorkspace | null {
  const located = findUnsealedAllocationBox(workspace, allocationId);
  if (located == null) return null;
  const { box: targetBox, allocation: existing } = located;

  const boxNext: TransferPackBox = {
    ...targetBox,
    allocations: targetBox.allocations.filter((row) => String(row.id) !== String(allocationId)),
  };
  const boxes = workspace.boxes.map((row) => (row.id === targetBox.id ? boxNext : row));
  const lines = patchLineQuantities(workspace.lines, existing.transfer_order_item_id, -existing.quantity);
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    lines,
    boxes,
    open_box,
    ...recomputePackFlags({ ...workspace, lines, boxes }),
  };
}

export function applyTransferPackBoxSealed(
  workspace: TransferPackWorkspace,
  boxId: number,
): TransferPackWorkspace | null {
  const target = workspace.boxes.find((row) => row.id === boxId);
  if (target == null) return null;
  const sealedAt = new Date().toISOString();
  const sealedBox: TransferPackBox = {
    ...target,
    sealed: true,
    sealed_at: sealedAt,
  };
  const boxes = workspace.boxes.map((row) => (row.id === boxId ? sealedBox : row));
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    boxes,
    open_box,
    ...recomputePackFlags({ ...workspace, boxes, open_box }),
  };
}

export function applyTransferPackBoxUnsealed(
  workspace: TransferPackWorkspace,
  boxId: number,
  statusReverted: boolean,
): TransferPackWorkspace | null {
  const target = workspace.boxes.find((row) => row.id === boxId);
  if (target == null) return null;
  const openBox: TransferPackBox = {
    ...target,
    sealed: false,
    sealed_at: null,
  };
  const boxes = workspace.boxes.map((row) => (row.id === boxId ? openBox : row));
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    status: statusReverted ? "packing" : workspace.status,
    boxes,
    open_box,
    ...recomputePackFlags({ ...workspace, boxes, open_box }),
  };
}

export function applyTransferPackCartonLabelPrinted(
  workspace: TransferPackWorkspace,
  boxId: number,
): TransferPackWorkspace | null {
  const target = workspace.boxes.find((row) => row.id === boxId);
  if (target == null) return null;
  const printedAt = new Date().toISOString();
  const boxes = workspace.boxes.map((row) => (
    row.id === boxId ? { ...row, label_printed_at: printedAt } : row
  ));
  const open_box = findPrimaryOpenTransferBox(boxes);
  return {
    ...workspace,
    boxes,
    open_box,
  };
}

export function applyTransferPackPendingRequestCreated(
  workspace: TransferPackWorkspace,
  transferOrderItemId: number,
): TransferPackWorkspace {
  return applyTransferPackPendingRequestsCreated(workspace, [transferOrderItemId]);
}

export function applyTransferPackPendingRequestsCreated(
  workspace: TransferPackWorkspace,
  transferOrderItemIds: readonly number[],
): TransferPackWorkspace {
  if (transferOrderItemIds.length === 0) return workspace;
  const idSet = new Set(transferOrderItemIds);
  return {
    ...workspace,
    pending_pack_qty_request_count: workspace.pending_pack_qty_request_count + transferOrderItemIds.length,
    lines: workspace.lines.map((line) => (
      idSet.has(line.id)
        ? { ...line, has_pending_pack_qty_request: true }
        : line
    )),
  };
}

export function applyTransferPackBoxStarted(
  workspace: TransferPackWorkspace,
  box: { id: number; box_number: number },
): TransferPackWorkspace {
  const openBox: TransferPackBox = {
    id: box.id,
    box_number: box.box_number,
    sealed: false,
    sealed_at: null,
    label_printed_at: null,
    allocations: [],
  };
  const boxes = [...workspace.boxes, openBox];
  return {
    ...workspace,
    boxes,
    open_box: openBox,
    ...recomputePackFlags({ ...workspace, boxes, open_box: openBox }),
  };
}
